import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import BrandMark from '@/components/generals/brand-mark'
import { useCart } from '@/components/shop/cart-provider'
import { mainNav, siteConfig, storeInfo } from '@/constants/site'

import {
    Clock,
    Menu,
    Phone,
    ShoppingBag,
    Store
} from 'lucide-react'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

const isActive = (pathname: string, href: string): boolean => {
    if (href.includes('#')) return false
    if (href === '/') return pathname === '/'

    return pathname === href || pathname.startsWith(`${href}/`)
}

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const { itemCount, setOpen } = useCart()
    const [menuOpen, setMenuOpen] = useState(false)

    return (
        <header className='sticky top-0 z-40'>
            <div className='bg-deep text-deep-foreground'>
                <div className='mx-auto flex h-9 w-full max-w-7xl items-center justify-center gap-6 px-5 text-xs font-medium sm:justify-between sm:px-8'>
                    <p className='flex items-center gap-2'>
                        <Store className='size-3.5 text-accent' />
                        Reserve online, pay when you pick up
                    </p>

                    <p className='hidden items-center gap-2 sm:flex'>
                        <Clock className='size-3.5 text-accent' />
                        Open early, every day of the week
                    </p>
                </div>
            </div>

            <div className='border-b border-border bg-background/90 backdrop-blur-md supports-backdrop-filter:bg-background/75'>
                <div className='mx-auto flex h-16 w-full max-w-7xl items-center gap-4 px-5 sm:h-[4.5rem] sm:px-8'>
                    <Sheet open={menuOpen} onOpenChange={open => setMenuOpen(open)}>
                        <SheetTrigger
                            render={
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    className='-ml-2 md:hidden'
                                    aria-label='Open menu'
                                />
                            }
                        >
                            <Menu className='size-5' />
                        </SheetTrigger>

                        <SheetContent side='left' className='gap-0 p-0'>
                            <SheetHeader className='border-b border-border pr-14'>
                                <SheetTitle className='flex items-center gap-2.5'>
                                    <BrandMark className='size-8' />
                                    {siteConfig.name}
                                </SheetTitle>

                                <SheetDescription>
                                    Tackle, bait and local know-how.
                                </SheetDescription>
                            </SheetHeader>

                            <nav aria-label='Mobile' className='flex flex-col p-3'>
                                {mainNav.map(item => (
                                    <Link
                                        key={item.href}
                                        href={item.href}
                                        onClick={() => setMenuOpen(false)}
                                        className={cn(
                                            'rounded-xl px-3 py-3 font-heading text-xl font-bold transition-colors hover:bg-muted',
                                            isActive(router.pathname, item.href) && 'bg-secondary text-secondary-foreground'
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </nav>

                            <div className='mt-auto space-y-3 border-t border-border p-5 text-sm'>
                                <p className='font-semibold'>
                                    Opening hours
                                </p>

                                <dl className='space-y-1.5 text-muted-foreground'>
                                    {storeInfo.hours.map(entry => (
                                        <div key={entry.days} className='flex justify-between gap-4'>
                                            <dt>
                                                {entry.days}
                                            </dt>

                                            <dd className='text-foreground'>
                                                {entry.time}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>

                                <a
                                    href={storeInfo.phoneHref}
                                    className='flex items-center gap-2 pt-2 font-semibold text-foreground'
                                >
                                    <Phone className='size-4' />
                                    {storeInfo.phone}
                                </a>
                            </div>
                        </SheetContent>
                    </Sheet>

                    <Link
                        href='/'
                        className='flex items-center gap-2.5 rounded-xl focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                    >
                        <BrandMark />

                        <span className='font-heading text-lg leading-none font-extrabold tracking-tight sm:text-xl'>
                            {siteConfig.name}
                        </span>
                    </Link>

                    <nav aria-label='Main' className='ml-6 hidden items-center gap-1 md:flex'>
                        {mainNav.map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                aria-current={isActive(router.pathname, item.href) ? 'page' : undefined}
                                className={cn(
                                    'rounded-full px-3.5 py-2 text-sm font-semibold text-foreground/75 transition-colors hover:bg-muted hover:text-foreground',
                                    isActive(router.pathname, item.href) && 'bg-secondary text-secondary-foreground hover:bg-secondary'
                                )}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='ml-auto flex items-center gap-2'>
                        <a
                            href={storeInfo.phoneHref}
                            className='hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-foreground/75 transition-colors hover:text-foreground lg:flex'
                        >
                            <Phone className='size-4' />
                            {storeInfo.phone}
                        </a>

                        <Button
                            variant='outline'
                            onClick={() => setOpen(true)}
                            aria-label={itemCount > 0 ? `Open cart, ${itemCount} items` : 'Open cart'}
                            className='relative pr-4 pl-3.5'
                        >
                            <ShoppingBag className='size-5' />

                            <span className='hidden sm:inline'>
                                Cart
                            </span>

                            {itemCount > 0 && (
                                <span className='absolute -top-1.5 -right-1.5 flex min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[0.7rem] leading-5 font-bold text-accent-foreground ring-2 ring-background'>
                                    {itemCount}
                                </span>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </header>
    )
}

export default SiteHeader
